/**
 * Inline Message Display Component
 *
 * Main orchestrating component that displays the message with
 * inline issue highlights and handles the complete workflow.
 *
 * ERROR HANDLING:
 * - Revision tracking errors are handled via `tracking.onTrackingError` callback
 * - All async tracking operations are wrapped in try-catch
 * - Tracking failures are non-blocking (UI continues to work)
 *
 * RECOMMENDED: Wrap this component with ErrorBoundary for defense-in-depth:
 * ```tsx
 * <ErrorBoundary fallback={<InlineMessageFallback />} componentName="InlineMessageDisplay">
 *   <InlineMessageDisplay {...props} />
 * </ErrorBoundary>
 * ```
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { View, Text, Platform } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '@/lib/analytics';
import { trackEvent } from '@/lib/analytics/events';
import type { DramaIssue, SuggestedRewrite, TierId } from '@dramadetector/shared';
import { useReducedMotionFallback } from '../../letter-throwing/hooks/useReducedMotionFallback';
import type {
  InlineIssue,
  ViewMode,
  TextSegment,
  RevisedSegment,
} from './types';
import { hasTierAccess } from '../../gating';
import { MessageSegments } from './MessageSegments';
import { IssueActionControls } from './IssueActionControls';
import { PlatformIssueDetail } from './PlatformIssueDetail';
import { useIssueInteraction } from './hooks/useIssueInteraction';
import { useIssueWorkflow } from './hooks/useIssueWorkflow';
import { mapSeverity } from './hooks/useScoreCalculation';
import { mergeIssuesWithRewrites } from './utils/mergeIssuesWithRewrites';
import { highlightText, highlightRevisedText } from './utils/highlightText';
import { headerStyles } from './inlineAnnotationsStyles';
import { AlmostThereNudge } from '../AlmostThereNudge';

/** Tier values accepted by this component (TierId + family_member from GatedInlineAnnotations) */
type TierProp = TierId | 'family_member';

/** Dummy position for programmatic issue taps (e.g., from nudge) */
const DUMMY_POSITION = { x: 0, y: 0, width: 0, height: 0 } as const;

/**
 * Revision tracking configuration (optional)
 * When provided, enables RLHF data collection for rewrite suggestions
 */
export interface RevisionTrackingConfig {
  /** Analysis ID from database (required) */
  analysisId: string;
  /** Original drama score (0-10) */
  originalScore: number;
  /** Original summary text */
  originalSummary?: string;
  /** Agent execution ID for attribution */
  agentExecutionId?: string;
  /** Model ID for filtering */
  modelId?: string;
  /** Whether tracking is enabled (defaults to true when config provided) */
  enabled?: boolean;
  /** Callback when tracking error occurs (for Sentry) */
  onTrackingError?: (error: Error, context: string) => void;
}

export interface InlineMessageDisplayProps {
  /** Original message text */
  message: string;
  /** Issues from API response */
  issues: DramaIssue[];
  /** Suggested rewrites from API response (optional - may be missing from API) */
  rewrites?: SuggestedRewrite[];
  /** User's tier for analytics */
  tier?: TierProp;
  /** Optional test ID */
  testID?: string;
  /** Optional revision tracking configuration (enables RLHF data collection) */
  tracking?: RevisionTrackingConfig;
  /** Screen name for analytics (used in tier-gating) */
  screen?: string;
  /** Callback when user clicks upgrade CTA (for tier-gated features) */
  onUpgrade?: () => void;
  /** Callback when score changes due to accept/ignore actions (Score Chase live delta) */
  onScoreChange?: (
    revisedScore: number,
    scoreDelta: number,
    meta?: { acceptedCount: number; isRescoring: boolean; sessionComplete: boolean; lastAcceptedSeverity?: string },
  ) => void;
}

/**
 * InlineMessageDisplay - Main inline annotations component
 */
export function InlineMessageDisplay({
  message,
  issues: apiIssues,
  rewrites = [],
  tier: tierProp = 'free',
  testID = 'inline-message-display',
  tracking,
  screen = 'InlineAnnotations',
  onUpgrade,
  onScoreChange,
}: InlineMessageDisplayProps) {
  const { t } = useTranslation('features');

  // Normalize tier: family_member inherits drama_plus entitlements
  const tier: TierId = tierProp === 'family_member' ? 'drama_plus' : tierProp;

  // Merge API data into unified issue structure
  // IMPORTANT: Pass message for index recalculation (LLM indices are unreliable)
  // FIX (2026-02-12): Normalize rewrites to empty array if undefined/null (DRAMA-WEB-2A)
  const initialIssues = useMemo(
    () => mergeIssuesWithRewrites(message, apiIssues, rewrites),
    [message, apiIssues, rewrites]
  );

  // Unified issue workflow with optional revision tracking
  const {
    issues,
    viewMode,
    revisedText,
    hasChanges,
    statusCounts,
    setViewMode,
    updateIssueStatus,
    scoreDelta,
    originalScore,
    revisedScore,
    isTrackingEnabled,
    isRescoring,
    revisionId,
  } = useIssueWorkflow({
    message,
    initialIssues,
    tracking,
    tier,
  });

  // Accessibility: detect reduced motion preference for analytics
  const isReducedMotionEnabled = useReducedMotionFallback();

  // Interaction management
  const {
    activeIssueId,
    anchorPosition,
    handlePress,
    deactivateIssue,
    setPopoverHovered,
  } = useIssueInteraction();

  // Bottom sheet state for native
  const [bottomSheetIndex, setBottomSheetIndex] = useState<number | null>(null);

  // Track analytics on mount
  useEffect(() => {
    captureEvent('InlineAnnotation_Shown', {
      issue_count: initialIssues.length,
      tier,
    });
  }, [initialIssues.length, tier]);

  // Track last accepted severity for narrator reactions
  const lastAcceptedSeverityRef = useRef<string | undefined>(undefined);

  // Fire onScoreChange callback with metadata for narrator + score chase
  const sessionComplete = issues.length > 0 &&
    statusCounts.accepted + statusCounts.ignored === issues.length;

  useEffect(() => {
    if (!onScoreChange) return;
    // Fire when score changes OR when isRescoring transitions
    if (scoreDelta > 0 || isRescoring) {
      onScoreChange(revisedScore, scoreDelta, {
        acceptedCount: statusCounts.accepted,
        isRescoring,
        sessionComplete,
        lastAcceptedSeverity: lastAcceptedSeverityRef.current,
      });
    }
  }, [onScoreChange, revisedScore, scoreDelta, isRescoring, statusCounts.accepted, sessionComplete]);

  // Track Paywall_ScoreDelta_Shown when free users see the blur teaser
  // (CTO mandate: Required for +15-25% conversion lift attribution)
  const hasTrackedPaywallRef = useRef(false);
  const userHasScoreDeltaAccess = hasTierAccess(tier, 'drama_plus');

  useEffect(() => {
    // Only track once per component instance when:
    // 1. Score delta exists (user accepted suggestions)
    // 2. User doesn't have access (will see blur)
    // 3. We have the onUpgrade prop (tracking is wired up)
    // 4. Haven't tracked yet
    if (
      scoreDelta > 0 &&
      !userHasScoreDeltaAccess &&
      onUpgrade &&
      !hasTrackedPaywallRef.current
    ) {
      hasTrackedPaywallRef.current = true;
      captureEvent('Paywall_ScoreDelta_Shown', {
        original_score: originalScore,
        issues_accepted: statusCounts.accepted,
        tier,
        screen,
      });
    }
  }, [
    scoreDelta,
    userHasScoreDeltaAccess,
    onUpgrade,
    originalScore,
    statusCounts.accepted,
    tier,
    screen,
  ]);

  // Click-only activation (hover behavior disabled for consistent cross-platform UX)

  // Handle press/click on highlight
  const handleHighlightPress = useCallback(
    (issueId: number, position: { x: number; y: number; width: number; height: number }) => {
      captureEvent('InlineAnnotation_IssueClicked', {
        issue_id: issueId,
        severity: issues.find((i) => i.id === issueId)?.severity,
        position: issueId,
      });

      if (Platform.OS === 'web') {
        handlePress(issueId, position);
      } else {
        // On native, open bottom sheet at this issue
        const index = issues.findIndex((i) => i.id === issueId);
        setBottomSheetIndex(index >= 0 ? index : null);
      }
    },
    [issues, handlePress]
  );

  // Handle accept
  const handleAccept = useCallback(
    (issueId: number) => {
      const issue = issues.find((i) => i.id === issueId);
      if (issue) {
        lastAcceptedSeverityRef.current = issue.severity;
      }
      updateIssueStatus(issueId, 'accepted');
      deactivateIssue();

      captureEvent('InlineAnnotation_SuggestionAccepted', {
        issue_id: issueId,
        drama_reduction: issue?.dramaReduction || 0,
      });

      // DD-550: Fire rewrite_accepted PostHog event for DPO training data
      if (issue) {
        trackEvent('Rewrite_Accepted', {
          suggestion_id: String(issue.id),
          original_text: issue.snippet,
          suggested_text: issue.replacement || '',
          issue_type: issue.type,
          severity: mapSeverity(issue.severity),
          revision_id: revisionId ?? undefined,
          tier: tier || 'free',
        });
      }
    },
    [issues, updateIssueStatus, deactivateIssue, revisionId, tier]
  );

  // Handle ignore
  const handleIgnore = useCallback(
    (issueId: number) => {
      const issue = issues.find((i) => i.id === issueId);
      updateIssueStatus(issueId, 'ignored');
      deactivateIssue();

      captureEvent('InlineAnnotation_SuggestionIgnored', {
        issue_id: issueId,
      });

      // DD-550: Fire rewrite_ignored PostHog event for DPO training data
      if (issue) {
        trackEvent('Rewrite_Ignored', {
          suggestion_id: String(issue.id),
          original_text: issue.snippet,
          suggested_text: issue.replacement || '',
          issue_type: issue.type,
          severity: mapSeverity(issue.severity),
          revision_id: revisionId ?? undefined,
          tier: tier || 'free',
        });
      }
    },
    [issues, updateIssueStatus, deactivateIssue, revisionId, tier]
  );

  // Handle view mode change
  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);

      captureEvent('InlineAnnotation_ViewToggled', {
        view_mode: mode,
      });
    },
    [setViewMode]
  );

  // Handle bottom sheet navigation
  const handlePrevious = useCallback(() => {
    if (bottomSheetIndex !== null && bottomSheetIndex > 0) {
      setBottomSheetIndex(bottomSheetIndex - 1);
    }
  }, [bottomSheetIndex]);

  const handleNext = useCallback(() => {
    if (bottomSheetIndex !== null && bottomSheetIndex < issues.length - 1) {
      setBottomSheetIndex(bottomSheetIndex + 1);
    }
  }, [bottomSheetIndex, issues.length]);

  // Close handler for native bottom sheet (clears index + deactivates issue)
  const handleCloseBottomSheet = useCallback(() => {
    setBottomSheetIndex(null);
    deactivateIssue();
  }, [deactivateIssue]);

  // Get active issue for popover
  const activeIssue = useMemo(
    () => issues.find((i) => i.id === activeIssueId) || null,
    [issues, activeIssueId]
  );

  // Get active issue index for web popover navigation
  const activeIssueIndex = useMemo(
    () => (activeIssueId !== null ? issues.findIndex((i) => i.id === activeIssueId) : -1),
    [issues, activeIssueId]
  );

  // Web popover navigation handlers
  const handleWebPrevious = useCallback(() => {
    if (activeIssueIndex > 0 && anchorPosition) {
      const prevIssue = issues[activeIssueIndex - 1];
      handlePress(prevIssue.id, anchorPosition);
    }
  }, [activeIssueIndex, issues, handlePress, anchorPosition]);

  const handleWebNext = useCallback(() => {
    if (activeIssueIndex >= 0 && activeIssueIndex < issues.length - 1 && anchorPosition) {
      const nextIssue = issues[activeIssueIndex + 1];
      handlePress(nextIssue.id, anchorPosition);
    }
  }, [activeIssueIndex, issues, handlePress, anchorPosition]);

  // Compute text segments for display based on view mode
  // Original view: show issue highlights (red/orange)
  // Revised view: show replacement highlights (green) for accepted changes
  const segments = useMemo((): (TextSegment | RevisedSegment)[] => {
    if (viewMode === 'original') {
      return highlightText(message, issues);
    }
    // Revised view: highlight accepted replacements in green
    return highlightRevisedText(message, revisedText, issues);
  }, [viewMode, message, revisedText, issues]);

  // Calculate addressed count
  const addressedCount = statusCounts.accepted + statusCounts.ignored;

  return (
    <View testID={testID}>
      {/* Header with issue count badge */}
      {issues.length > 0 && (
        <View style={headerStyles.header}>
          <Text style={headerStyles.headerTitle}>
            {t('inline_annotations.header', 'Your Message')}
          </Text>
          <View style={headerStyles.issueBadge}>
            <Text style={headerStyles.issueBadgeText}>
              {t('inline_annotations.issues_count', {
                count: issues.length,
              } as any)}
            </Text>
          </View>
        </View>
      )}

      {/* Issue Action Controls (Segmented Control + Progress + Score Delta + Copy Bar) */}
      <IssueActionControls
        viewMode={viewMode}
        issues={issues}
        statusCounts={statusCounts}
        revisedText={revisedText}
        hasChanges={hasChanges}
        addressedCount={addressedCount}
        scoreDelta={scoreDelta}
        originalScore={originalScore}
        revisedScore={revisedScore}
        isTrackingEnabled={isTrackingEnabled}
        isReducedMotionEnabled={isReducedMotionEnabled}
        tier={tier}
        screen={screen}
        onViewModeChange={handleViewModeChange}
        onUpgrade={onUpgrade}
        testID={testID}
      />

      {/* Message Display with inline highlights */}
      <MessageSegments
        segments={segments}
        activeIssueId={activeIssueId}
        handleHighlightPress={handleHighlightPress}
        testID={testID}
      />

      {/* Contextual nudge when near category threshold */}
      {viewMode === 'original' && isTrackingEnabled && (
        <AlmostThereNudge
          revisedScore={revisedScore}
          originalScore={originalScore}
          issues={issues}
          isReducedMotion={isReducedMotionEnabled}
          onIssueTap={(issueId) => handleHighlightPress(issueId, DUMMY_POSITION)}
          testID={`${testID}-almost-there-nudge`}
        />
      )}

      {/* Platform-specific Issue Detail View (Web Popover / Native Bottom Sheet) */}
      <PlatformIssueDetail
        issues={issues}
        activeIssue={activeIssue}
        activeIssueIndex={activeIssueIndex}
        anchorPosition={anchorPosition}
        bottomSheetIndex={bottomSheetIndex}
        onAccept={handleAccept}
        onIgnore={handleIgnore}
        onClose={Platform.OS === 'web' ? deactivateIssue : handleCloseBottomSheet}
        onPopoverMouseEnter={() => setPopoverHovered(true)}
        onPopoverMouseLeave={() => setPopoverHovered(false)}
        onWebPrevious={handleWebPrevious}
        onWebNext={handleWebNext}
        onNativePrevious={handlePrevious}
        onNativeNext={handleNext}
        testID={testID}
      />
    </View>
  );
}
